import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, ChevronRight, ExternalLink, Briefcase, FileText, Download } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fixed height for the component to match Useful Links
const FIXED_HEIGHT = '400px';
const CONTENT_HEIGHT = '280px';

// Check if date is in future for NEW badge
const isNew = (lastDate) => {
  if (!lastDate) return false;
  const today = new Date();
  const last = new Date(lastDate);
  return last >= today;
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Tab configurations with their specific columns and view links
const tabConfigs = {
  announcements: {
    label: 'Announcements',
    viewLink: '/announcements',
    icon: Megaphone,
    columns: [
      { key: 'serial', label: 'S. NO', width: 'w-16 text-center flex-shrink-0' },
      { key: 'title', label: 'TITLE', width: 'flex-1 min-w-0' },
      { key: 'last_date', label: 'LAST DATE', width: 'w-28 text-center flex-shrink-0' }
    ]
  },
  jobs: {
    label: 'Jobs',
    viewLink: '/jobs',
    icon: Briefcase,
    columns: [
      { key: 'serial', label: 'S. NO', width: 'w-14 text-center flex-shrink-0' },
      { key: 'title', label: 'TITLE', width: 'flex-1 min-w-0' },
      { key: 'notification', label: 'NOTIFICATION', width: 'w-24 text-center flex-shrink-0' },
      { key: 'application', label: 'APP. FORM', width: 'w-24 text-center flex-shrink-0' },
      { key: 'last_date', label: 'LAST DATE', width: 'w-28 text-center flex-shrink-0' },
      { key: 'result', label: 'RESULT', width: 'w-20 text-center flex-shrink-0' }
    ]
  },
  tenders: {
    label: 'Tenders',
    viewLink: '/tenders',
    icon: FileText,
    columns: [
      { key: 'serial', label: 'S. NO', width: 'w-16 text-center flex-shrink-0' },
      { key: 'title', label: 'TITLE', width: 'flex-1 min-w-0' },
      { key: 'tender_pdf', label: 'TENDER PDF', width: 'w-24 text-center flex-shrink-0' },
      { key: 'last_date', label: 'LAST DATE', width: 'w-28 text-center flex-shrink-0' }
    ]
  }
};

export default function KeyOfferings({ announcements = [], jobs = [], tenders = [] }) {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('announcements');

  const tabs = [
    { id: 'announcements', label: 'Announcements', data: announcements },
    { id: 'jobs', label: 'Jobs', data: jobs },
    { id: 'tenders', label: 'Tenders', data: tenders },
  ];

  const activeData = tabs.find(tab => tab.id === activeTab)?.data || [];
  const currentConfig = tabConfigs[activeTab];

  // Get the link URL for an item
  const getItemLink = (item) => {
    return item.link_url || item.form_file_url || item.pdf_url || item.external_link || '#';
  };

  // Check if link is external
  const isExternalLink = (url) => {
    if (!url || url === '#') return false;
    return url.startsWith('http') || url.startsWith('/uploads') || url.startsWith('/api/uploads');
  };

  // Handle file download
  const handleFileDownload = (e, fileUrl, fileName = 'document') => {
    e.stopPropagation();
    if (!fileUrl) return;
    
    // Construct full URL if relative
    const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_URL}${fileUrl}`;
    
    // Open file in new tab
    window.open(fullUrl, '_blank');
  };

  // Handle job title click - navigate to Jobs page
  const handleJobTitleClick = (e, jobId) => {
    e.preventDefault();
    window.location.href = `/jobs#job-${jobId}`;
  };

  // Handle tender title click - navigate to Tenders page
  const handleTenderTitleClick = (e, tenderId) => {
    e.preventDefault();
    window.location.href = `/tenders#tender-${tenderId}`;
  };

  // Render file download button
  const renderFileButton = (fileUrl, label) => {
    if (!fileUrl) return null;
    
    return (
      <button
        onClick={(e) => handleFileDownload(e, fileUrl, label)}
        className="inline-flex items-center justify-center p-1.5 text-[#0E6D2B] hover:bg-green-50 rounded transition-colors"
        title={`Download ${label}`}
        aria-label={`Download ${label}`}
      >
        <Download className="w-4 h-4" />
      </button>
    );
  };

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 flex flex-col" 
      style={{ height: FIXED_HEIGHT }}
      data-testid="key-offerings"
    >
      {/* Section heading with View button - Green background matching Useful Links */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#E8F5E9] rounded-t-lg border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#0E6D2B]" />
          <h2 className="text-sm md:text-base font-bold text-[#0E6D2B] uppercase">{t('keyOfferings')}</h2>
        </div>
        {/* View All button - navigates to the current tab's page */}
        <Link
          to={currentConfig.viewLink}
          className="bg-[#0E6D2B] text-white px-3 py-1.5 text-xs font-semibold rounded hover:bg-[#0a5a23] flex items-center gap-1 transition-colors"
          data-testid="view-all-btn"
        >
          View All {currentConfig.label}
          <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 px-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === tab.id 
                ? 'text-[#0E6D2B]' 
                : 'text-gray-500 hover:text-[#0E6D2B]'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0E6D2B]" />
            )}
          </button>
        ))}
      </div>

      {/* Table content - Fixed height with scroll */}
      <div className="flex-1 overflow-hidden">
        {activeData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-500 text-sm">
            No {activeTab} available at the moment.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:flex flex-col h-full">
              {/* Table Header - Bold styling */}
              <div className="flex items-center border-b border-gray-200 bg-gray-50 flex-shrink-0">
                {currentConfig.columns.map((col) => (
                  <div 
                    key={col.key} 
                    className={`px-3 py-2.5 text-[11px] font-bold text-gray-700 uppercase tracking-wide ${col.width}`}
                  >
                    {col.label}
                  </div>
                ))}
              </div>
              
              {/* Table Body - Scrollable */}
              <div className="flex-1 overflow-y-auto custom-scroll" style={{ maxHeight: CONTENT_HEIGHT }}>
                {activeData.slice(0, 20).map((item, index) => {
                  const link = getItemLink(item);
                  const isExternal = isExternalLink(link);
                  const isJobsTab = activeTab === 'jobs';
                  const isTendersTab = activeTab === 'tenders';
                  
                  return (
                    <div 
                      key={item.id || index} 
                      className="flex items-center border-b border-gray-50 hover:bg-gray-50 transition-colors min-h-[48px]"
                      data-testid={`offering-row-${index}`}
                    >
                      {/* Serial Number */}
                      <div className={`px-2 py-3 text-center text-xs text-gray-600 font-medium flex-shrink-0 ${
                        isJobsTab ? 'w-14' : 'w-16'
                      }`}>
                        {index + 1}
                      </div>
                      
                      {/* Title */}
                      <div className="px-3 py-3 flex-1 min-w-0">
                        <div className="flex items-start gap-1.5">
                          {isJobsTab ? (
                            <a
                              href={`/jobs#job-${item.id}`}
                              onClick={(e) => handleJobTitleClick(e, item.id)}
                              className="text-xs text-[#0E6D2B] hover:underline flex items-start gap-1 group leading-tight font-medium"
                            >
                              <span className="flex-1 line-clamp-2">{item.title}</span>
                            </a>
                          ) : isTendersTab ? (
                            <a
                              href={`/tenders#tender-${item.id}`}
                              onClick={(e) => handleTenderTitleClick(e, item.id)}
                              className="text-xs text-[#0E6D2B] hover:underline flex items-start gap-1 group leading-tight font-medium"
                            >
                              <span className="flex-1 line-clamp-2">{item.title}</span>
                            </a>
                          ) : (
                            <a
                              href={link}
                              target={isExternal ? '_blank' : '_self'}
                              rel={isExternal ? 'noopener noreferrer' : undefined}
                              className="text-xs text-[#0E6D2B] hover:underline flex items-start gap-1 group leading-tight font-medium"
                            >
                              <span className="flex-1 line-clamp-2">{item.title}</span>
                              {isExternal && (
                                <ExternalLink className="w-3 h-3 flex-shrink-0 mt-0.5 text-gray-400 group-hover:text-[#0E6D2B]" />
                              )}
                            </a>
                          )}
                          {isNew(item.last_date) && (
                            <span 
                              className="bg-green-500 text-white text-[9px] px-1.5 py-0.5 rounded font-bold badge-new-flash flex-shrink-0" 
                              data-testid="new-badge"
                            >
                              NEW
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Jobs-specific columns */}
                      {isJobsTab && (
                        <>
                          {/* Notification */}
                          <div className="px-2 py-3 w-24 text-center flex-shrink-0">
                            {renderFileButton(item.pdf_url, 'Notification')}
                          </div>
                          
                          {/* Application Form */}
                          <div className="px-2 py-3 w-24 text-center flex-shrink-0">
                            {renderFileButton(item.form_file_url, 'Application Form')}
                          </div>
                        </>
                      )}
                      
                      {/* Tenders-specific Tender PDF column */}
                      {isTendersTab && (
                        <div className="px-2 py-3 w-24 text-center flex-shrink-0">
                          {renderFileButton(item.pdf_url || item.tender_pdf_url, 'Tender PDF')}
                        </div>
                      )}
                      
                      {/* Last Date */}
                      <div className={`px-2 py-3 text-center text-xs text-gray-600 flex-shrink-0 ${
                        isJobsTab ? 'w-28' : 'w-28'
                      }`}>
                        {formatDate(item.last_date)}
                      </div>
                      
                      {/* Result (Jobs only) */}
                      {isJobsTab && (
                        <div className="px-2 py-3 w-20 text-center flex-shrink-0">
                          {renderFileButton(item.result_pdf_url, 'Result')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile list view */}
            <div className="md:hidden h-full overflow-y-auto custom-scroll">
              {activeData.slice(0, 20).map((item, index) => {
                const link = getItemLink(item);
                const isExternal = isExternalLink(link);
                const isJobsTab = activeTab === 'jobs';
                const isTendersTab = activeTab === 'tenders';
                
                return (
                  <div
                    key={item.id || index}
                    className="block p-3 border-b border-gray-50 hover:bg-gray-50 transition-colors"
                    data-testid={`offering-mobile-${index}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-[10px] text-gray-400 w-4 flex-shrink-0 mt-0.5">{index + 1}.</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-1">
                          {isJobsTab ? (
                            <a
                              href={`/jobs#job-${item.id}`}
                              onClick={(e) => handleJobTitleClick(e, item.id)}
                              className="text-xs text-gray-800 font-medium flex-1 line-clamp-2 hover:text-[#0E6D2B]"
                            >
                              {item.title}
                            </a>
                          ) : isTendersTab ? (
                            <a
                              href={`/tenders#tender-${item.id}`}
                              onClick={(e) => handleTenderTitleClick(e, item.id)}
                              className="text-xs text-gray-800 font-medium flex-1 line-clamp-2 hover:text-[#0E6D2B]"
                            >
                              {item.title}
                            </a>
                          ) : (
                            <a
                              href={link}
                              target={isExternal ? '_blank' : '_self'}
                              rel={isExternal ? 'noopener noreferrer' : undefined}
                              className="text-xs text-gray-800 font-medium flex-1 line-clamp-2 hover:text-[#0E6D2B]"
                            >
                              {item.title}
                            </a>
                          )}
                          {isNew(item.last_date) && (
                            <span className="bg-green-500 text-white text-[8px] px-1 py-0.5 rounded font-bold badge-new-flash flex-shrink-0">
                              NEW
                            </span>
                          )}
                        </div>
                        {item.last_date && (
                          <p className="text-[10px] text-gray-400 mt-1">
                            Last Date: {formatDate(item.last_date)}
                          </p>
                        )}
                        {/* Jobs file buttons on mobile */}
                        {isJobsTab && (item.pdf_url || item.form_file_url || item.result_pdf_url) && (
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {item.pdf_url && (
                              <button
                                onClick={(e) => handleFileDownload(e, item.pdf_url, 'Notification')}
                                className="text-[10px] text-[#0E6D2B] font-medium flex items-center gap-1 hover:underline"
                              >
                                <Download className="w-3 h-3" />
                                Notification
                              </button>
                            )}
                            {item.form_file_url && (
                              <button
                                onClick={(e) => handleFileDownload(e, item.form_file_url, 'Form')}
                                className="text-[10px] text-[#0E6D2B] font-medium flex items-center gap-1 hover:underline"
                              >
                                <Download className="w-3 h-3" />
                                Form
                              </button>
                            )}
                            {item.result_pdf_url && (
                              <button
                                onClick={(e) => handleFileDownload(e, item.result_pdf_url, 'Result')}
                                className="text-[10px] text-[#0E6D2B] font-medium flex items-center gap-1 hover:underline"
                              >
                                <Download className="w-3 h-3" />
                                Result
                              </button>
                            )}
                          </div>
                        )}
                        {/* Tenders file button on mobile */}
                        {isTendersTab && (item.pdf_url || item.tender_pdf_url) && (
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={(e) => handleFileDownload(e, item.pdf_url || item.tender_pdf_url, 'Tender PDF')}
                              className="text-[10px] text-[#0E6D2B] font-medium flex items-center gap-1 hover:underline"
                            >
                              <Download className="w-3 h-3" />
                              Tender PDF
                            </button>
                          </div>
                        )}
                      </div>
                      {!isJobsTab && !isTendersTab && <ExternalLink className="w-3 h-3 text-gray-300 flex-shrink-0 mt-0.5" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
